import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Phone } from 'lucide-react';
import EnclosurePreview from '../EnclosurePreview';

const DimensionsStep = ({ config, updateConfig, updateDimensions }) => (
  <div className="grid md:grid-cols-2 gap-8">
    <div>
      <h3 className="text-xl font-semibold mb-6">Enter Dimensions</h3>

      {/* Hinge Position */}
      {config.enclosureType !== 'sliding' && config.enclosureType !== 'neo-angle' && (
        <div className="mb-6">
          <Label className="mb-2 block">Hinge Position</Label>
          <div className="flex gap-3">
            <Button type="button" variant={config.hingePosition === 'left' ? 'default' : 'outline'} onClick={() => updateConfig('hingePosition', 'left')} className="flex-1">Hinge Left</Button>
            <Button type="button" variant={config.hingePosition === 'right' ? 'default' : 'outline'} onClick={() => updateConfig('hingePosition', 'right')} className="flex-1">Hinge Right</Button>
          </div>
        </div>
      )}

      {/* Door Position */}
      {config.enclosureType === 'door-panel' && (
        <div className="mb-6">
          <Label className="mb-2 block">Door Position</Label>
          <div className="flex gap-3">
            <Button type="button" variant={config.doorPosition === 'left' ? 'default' : 'outline'} onClick={() => updateConfig('doorPosition', 'left')} className="flex-1">Door on Left</Button>
            <Button type="button" variant={config.doorPosition === 'right' ? 'default' : 'outline'} onClick={() => updateConfig('doorPosition', 'right')} className="flex-1">Door on Right</Button>
          </div>
        </div>
      )}

      {/* Complex installation */}
      <Card
        className={`mb-6 cursor-pointer transition-all ${config.isComplex ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-gray-50'}`}
        onClick={() => updateConfig('isComplex', !config.isComplex)}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${config.isComplex ? 'bg-primary border-primary' : 'border-gray-300'}`}>
              {config.isComplex && <Check className="h-3 w-3 text-white" />}
            </div>
            <div>
              <p className="font-medium">More complex installation?</p>
              <p className="text-sm text-muted-foreground">Contact us to discuss your specific requirements</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {!config.isComplex && (
        <div className="space-y-4">
          {config.enclosureType === 'neo-angle' ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="side1">Side 1 (inches) *</Label>
                  <Input id="side1" type="number" placeholder="e.g., 24" value={config.dimensions.side1} onChange={(e) => updateDimensions('side1', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="front">Front/Door (inches) *</Label>
                  <Input id="front" type="number" placeholder="e.g., 28" value={config.dimensions.front} onChange={(e) => updateDimensions('front', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="side2">Side 2 (inches) *</Label>
                  <Input id="side2" type="number" placeholder="e.g., 24" value={config.dimensions.side2} onChange={(e) => updateDimensions('side2', e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="height">Height (inches) *</Label>
                <Input id="height" type="number" placeholder="e.g., 72" value={config.dimensions.height} onChange={(e) => updateDimensions('height', e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="width">Overall Width (inches) *</Label>
                  <Input id="width" type="number" placeholder="e.g., 60" value={config.dimensions.width} onChange={(e) => updateDimensions('width', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="height">Height (inches) *</Label>
                  <Input id="height" type="number" placeholder="e.g., 72" value={config.dimensions.height} onChange={(e) => updateDimensions('height', e.target.value)} />
                </div>
              </div>
              {config.enclosureType !== 'single-door' && config.enclosureType !== 'sliding' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="doorWidth">Door Width (inches)</Label>
                    <Input id="doorWidth" type="number" placeholder="28" value={config.dimensions.doorWidth} onChange={(e) => updateDimensions('doorWidth', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="panelWidth">Panel Width (inches)</Label>
                    <Input id="panelWidth" type="number" placeholder="Auto-calculated" value={config.dimensions.panelWidth} readOnly className="bg-gray-50" />
                    <p className="text-xs text-muted-foreground mt-1">Auto-calculated from width - door</p>
                  </div>
                </div>
              )}
            </>
          )}
          <p className="text-sm text-muted-foreground mt-4">
            * Not sure about measurements? No problem! We offer free in-home consultations to measure your space.
          </p>
        </div>
      )}

      {config.isComplex && (
        <div className="bg-primary/5 rounded-lg p-4 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <Phone className="h-5 w-5 text-primary" />
            <span className="font-medium">We'll discuss your project in detail</span>
          </div>
          <p className="text-sm text-muted-foreground">
            For complex installations with unique angles, multiple doors, or special requirements,
            our team will work with you to design the perfect solution. Continue to submit your
            contact info and we'll schedule a consultation.
          </p>
        </div>
      )}
    </div>

    <div className="flex items-center justify-center">
      <div className="bg-gray-50 rounded-lg p-8 w-full max-w-xs">
        <EnclosurePreview type={config.enclosureType} hingePosition={config.hingePosition} doorPosition={config.doorPosition} />
        <p className="text-center text-sm text-muted-foreground mt-4">
          {config.isComplex ? 'Complex installation' :
            config.enclosureType === 'neo-angle'
              ? (config.dimensions.side1 && config.dimensions.front && config.dimensions.side2
                  ? `${config.dimensions.side1}" × ${config.dimensions.front}" × ${config.dimensions.side2}"`
                  : 'Enter dimensions to preview')
              : (config.dimensions.width && config.dimensions.height
                  ? `${config.dimensions.width}" × ${config.dimensions.height}"`
                  : 'Enter dimensions to preview')}
        </p>
      </div>
    </div>
  </div>
);

export default DimensionsStep;
